import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLogOut, FiRefreshCw, FiUser, FiClock, FiCheckCircle, FiGrid, FiShoppingBag, FiCoffee, FiPlus, FiSearch } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CaptainDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tables');
  const [profile, setProfile] = useState(null);
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [openTables, setOpenTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [newOrder, setNewOrder] = useState({
    customerName: '',
    customerPhone: '',
    orderType: 'dine-in',
    tableNumber: '',
    items: [],
    paymentMethod: 'cash',
    gstNumber: ''
  });
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availableTables, setAvailableTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');

    if (!token || userRole !== 'captain') {
      navigate('/login');
      return;
    }

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  // Fetch menu items and categories when modal opens
  useEffect(() => {
    if (showCreateOrderModal) {
      fetchMenuItems();
      fetchCategories();
      fetchAvailableTables();
    }
  }, [showCreateOrderModal]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, tablesRes, ordersRes, openTablesRes] = await Promise.all([
        api.get('/captain/profile'),
        api.get('/captain/tables'),
        api.get('/captain/orders'),
        api.get('/captain/open-tables')
      ]);

      setProfile(profileRes.data.data);
      setTables(tablesRes.data.data || []);
      setOrders(ordersRes.data.data || []);
      setOpenTables(openTablesRes.data.data || []);

      // Show success message for manual refresh
      toast.success('Dashboard refreshed successfully!');
    } catch (error) {
      toast.error('Failed to fetch data');
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/captain/orders/${orderId}/status`, { status: newStatus });
      toast.success('Order status updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  // Fetch functions for Create Order modal
  const fetchMenuItems = async () => {
    setLoadingMenuItems(true);
    try {
      console.log('Fetching menu items from /captain/menu-items...');
      const response = await api.get('/captain/menu-items');
      console.log('Menu items response:', response.data);
      setMenuItems(response.data.data || []);
      toast.success(`Loaded ${response.data.data?.length || 0} menu items`);
    } catch (error) {
      console.error('Error fetching menu items:', error.response?.data || error.message);
      // Fallback to mock data
      const mockItems = [
        { _id: '1', name: 'Tea', price: 20, categoryId: '1' },
        { _id: '2', name: 'Coffee', price: 30, categoryId: '1' },
        { _id: '3', name: 'Sandwich', price: 50, categoryId: '2' },
        { _id: '4', name: 'Burger', price: 80, categoryId: '2' },
        { _id: '5', name: 'Pizza', price: 150, categoryId: '3' },
        { _id: '6', name: 'Pasta', price: 120, categoryId: '3' }
      ];
      setMenuItems(mockItems);
      toast.error('Using sample menu items (API failed)');
    } finally {
      setLoadingMenuItems(false);
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      console.log('Fetching categories from /captain/categories...');
      const response = await api.get('/captain/categories');
      console.log('Categories response:', response.data);
      setCategories(response.data.data || []);
      toast.success(`Loaded ${response.data.data?.length || 0} categories`);
    } catch (error) {
      console.error('Error fetching categories:', error.response?.data || error.message);
      // Fallback to mock data
      const mockCategories = [
        { _id: '1', name: 'Beverages' },
        { _id: '2', name: 'Fast Food' },
        { _id: '3', name: 'Main Course' }
      ];
      setCategories(mockCategories);
      toast.error('Using sample categories (API failed)');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Add item to order function
  const addItemToOrder = (item) => {
    setNewOrder(prevOrder => {
      const existingItem = prevOrder.items.find(orderItem => orderItem._id === item._id);

      if (existingItem) {
        // Update quantity if item already exists
        return {
          ...prevOrder,
          items: prevOrder.items.map(orderItem =>
            orderItem._id === item._id
              ? { ...orderItem, quantity: orderItem.quantity + 1, subtotal: (orderItem.quantity + 1) * orderItem.price }
              : orderItem
          )
        };
      } else {
        // Add new item
        return {
          ...prevOrder,
          items: [...prevOrder.items, { ...item, quantity: 1, subtotal: item.price }]
        };
      }
    });
    toast.success(`${item.name} added to order`);
  };

  // Update item quantity
  const updateItemQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      removeItemFromOrder(itemId);
      return;
    }

    setNewOrder(prevOrder => ({
      ...prevOrder,
      items: prevOrder.items.map(item =>
        item._id === itemId
          ? { ...item, quantity: newQuantity, subtotal: newQuantity * item.price }
          : item
      )
    }));
  };

  // Create order function
  const handleCreateOrder = async () => {
    try {
      // Validation
      if (!newOrder.customerPhone) {
        toast.error('Customer phone is required');
        return;
      }

      if (!newOrder.orderType) {
        toast.error('Order type is required');
        return;
      }

      if (newOrder.orderType === 'dine-in' && !newOrder.tableNumber) {
        toast.error('Table selection is required for dine-in orders');
        return;
      }

      if (newOrder.items.length === 0) {
        toast.error('At least one item is required');
        return;
      }

      // Create order data with correct structure
      const orderData = {
        customerName: newOrder.customerName,
        customerPhone: newOrder.customerPhone,
        orderType: newOrder.orderType,
        tableNumber: newOrder.tableNumber,
        items: newOrder.items.map(item => ({
          menuItemId: item._id,
          name: item.name,
          price: item.price,
          quantity: item.quantity || 1
        })),
        paymentMethod: newOrder.paymentMethod,
        gstNumber: newOrder.gstNumber
      };

      console.log('Creating order with data:', orderData);

      // Call API
      const response = await api.post('/captain/orders', orderData);

      toast.success('Order created successfully!');
      console.log('Order created:', response.data);

      // Close modal and reset form
      setShowCreateOrderModal(false);
      setNewOrder({
        customerName: '',
        customerPhone: '',
        orderType: 'dine-in',
        tableNumber: '',
        items: [],
        paymentMethod: 'cash',
        gstNumber: ''
      });

      // Refresh dashboard data
      fetchData();

    } catch (error) {
      console.error('Error creating order:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create order';
      toast.error(errorMessage);
    }
  };

  // Remove item from order
  const removeItemFromOrder = (itemId) => {
    setNewOrder(prevOrder => ({
      ...prevOrder,
      items: prevOrder.items.filter(item => item._id !== itemId)
    }));
    toast.success('Item removed from order');
  };

  const fetchAvailableTables = async () => {
    try {
      // Use captain's assigned tables instead of vendor available tables
      const response = await api.get('/captain/tables');
      setAvailableTables(response.data.data || []);
    } catch (error) {
      console.error('Error fetching available tables:', error);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      accepted: 'bg-blue-100 text-blue-800 border-blue-200',
      preparing: 'bg-purple-100 text-purple-800 border-purple-200',
      ready: 'bg-green-100 text-green-800 border-green-200',
      completed: 'bg-gray-100 text-gray-800 border-gray-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading && !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Helper function for title case (each word capitalized)
  const toTitleCase = (str) => {
    if (!str) return '';
    return str.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const filteredItems = menuItems.filter((item) => {
    const matchesCategory =
      !selectedCategory ||
      item.categoryId === selectedCategory ||
      item.categoryId?._id === selectedCategory;

    const matchesSearch =
      !itemSearchTerm ||
      item.name?.toLowerCase().includes(itemSearchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  // Create Order Modal
  if (showCreateOrderModal) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 z-50">
        <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
          {/* HEADER */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 sticky top-0 z-10">
            <h2 className="text-xl text-gray-800">
              Create New Order
            </h2>
            <button
              onClick={() => setShowCreateOrderModal(false)}
              className="text-gray-500 hover:text-red-500 text-2xl transition"
            >
              ×
            </button>
          </div>

          {/* BODY */}
          <div className="p-6 overflow-y-auto custom-scroll">
            {/* FORM SECTION */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-1">
                  <label className="block text-sm mb-1">
                    Customer Phone *
                  </label>
                  <input
                    type="tel"
                    maxLength={10}
                    value={newOrder.customerPhone}
                    onChange={(e) =>
                      setNewOrder({
                        ...newOrder,
                        customerPhone: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder="10-digit phone number"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-grey-500 focus:border-grey-500 outline-none"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm mb-1">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={newOrder.customerName}
                    onChange={(e) =>
                      setNewOrder({ ...newOrder, customerName: e.target.value })
                    }
                    placeholder="Optional"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-1 focus:ring-grey-500 focus:border-grey-500 outline-none"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm mb-1">
                    Order Type *
                  </label>
                  <select
                    value={newOrder.orderType}
                    onChange={(e) =>
                      setNewOrder({ ...newOrder, orderType: e.target.value })
                    }
                    className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-1 focus:ring-grey-500 outline-none"
                  >
                    <option value="dine-in">Dine-In</option>
                    <option value="pickup">Pickup</option>
                    <option value="delivery">Delivery</option>
                  </select>
                </div>
                {newOrder.orderType === "dine-in" && (
                  <div className="md:col-span-1">
                    <label className="block text-sm mb-1">
                      Select Table *
                    </label>
                    <select
                      value={newOrder.tableNumber}
                      onChange={(e) =>
                        setNewOrder({ ...newOrder, tableNumber: e.target.value })
                      }
                      className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-1 focus:ring-grey-500 outline-none"
                    >
                      <option value="">Select table</option>
                      {availableTables.map((table) => (
                        <option 
                          key={table._id} 
                          value={table.tableNumber ?? table._id} 
                          className="text-sm"
                        >
                          Table {table.tableNumber ?? table._id} 
                          {table.seatingCapacity ? ` (${table.seatingCapacity} seats)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* 3 COLUMN LAYOUT */}
            <div className="hidden md:block">
              <div className="grid grid-cols-[2.5fr_6fr_3.5fr] !gap-2 border-b border-gray-200">
                {/* COLUMN 1: CATEGORIES */}
                <div className="pr-2 border-r border-red-300">
                  <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                    Categories
                  </h3>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto custom-scroll">
                    {/* All Categories */}
                    <button
                      onClick={() => setSelectedCategory('')}
                      className={`w-full text-left border rounded-lg px-3 py-2 text-xs transition
                        ${!selectedCategory
                          ? "bg-blue-500 text-white border-blue-500 shadow"
                          : "bg-white hover:bg-blue-50 border-gray-300"
                        }`}
                    >
                      All Categories
                    </button>

                    {categories.map((category) => (
                      <button
                        key={category._id}
                        onClick={() => setSelectedCategory(category._id)}
                        className={`w-full text-left border rounded-lg px-1 py-1 text-sm transition
                          ${selectedCategory === category._id
                            ? "bg-blue-500 text-white border-blue-500 shadow"
                            : "bg-white hover:bg-blue-50 border-gray-300"
                          }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* COLUMN 2: ITEMS */}
                <div className="border-r border-red-300 pr-2">
                  <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                    Menu Items
                  </h3>
                  <div className="relative mb-2">
                    <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={itemSearchTerm}
                      onChange={(e) => setItemSearchTerm(e.target.value)}
                      className="w-full border rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-blue-600 outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-3 !gap-2 max-h-[420px] overflow-y-auto custom-scroll">
                  {loadingMenuItems ? (
                    <div className="col-span-3 text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <p className="text-sm text-gray-500 mt-2">Loading menu items...</p>
                    </div>
                  ) : filteredItems.length > 0 ? (
                    filteredItems.map((item) => {
                      const isSelected = newOrder.items.some(orderItem => orderItem._id === item._id);
                      return (
                        <div
                          key={item._id}
                          onClick={() => addItemToOrder(item)}
                          className={`border rounded-lg p-3 cursor-pointer transition-all ${
                            isSelected
                              ? "bg-blue-50 border-blue-400 shadow-md"
                              : "bg-white hover:bg-gray-50 hover:shadow-sm"
                          }`}
                        >
                          <div className="flex flex-col gap-1">
                            
                            {/* Item Name */}
                            <p className="text-xs font-medium line-clamp-2">
                              {item.name}
                            </p>

                            {/* Price */}
                            <p className="text-blue-500 text-sm font-semibold">
                              ₹{item.discountPrice || item.price}
                            </p>

                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-3 text-center py-8">
                      <div className="text-3xl mb-2">🔍</div>
                      <p className="text-gray-600 text-sm mb-1">
                        No items found
                      </p>
                      <p className="text-gray-400 text-xs">
                        {selectedCategory && itemSearchTerm
                          ? `No items found in this category matching "${itemSearchTerm}"`
                          : selectedCategory
                            ? "No items available in this category"
                            : itemSearchTerm
                              ? `No items matching "${itemSearchTerm}"`
                              : "No items available"}
                      </p>
                    </div>
                  )}
                </div>
                </div>

                {/* COLUMN 3: ORDER ITEMS */}
                <div className="">
                  <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                    Order Items ({newOrder.items.length})
                  </h3>
                  <div className="divide-y max-h-[450px] overflow-y-auto custom-scroll">
                    {newOrder.items.length > 0 ? (
                      newOrder.items.map((item) => (
                        <div key={item._id} className="p-1">
                          {/* Name + Price + Delete */}
                          <div className="flex items-center justify-between mb-0">
                            <p className="text-xs truncate">
                              {item.name}
                            </p>
                            <div className="flex items-center !gap-0">
                              <button
                                onClick={() => updateItemQuantity(item._id, (item.quantity || 1) - 1)}
                                className="w-6 h-6 bg-gray-200 rounded text-sm"
                              >
                                -
                              </button>
                              <span className="text-xs w-6 text-center">
                                {item.quantity || 1}
                              </span>
                              <button
                                onClick={() => updateItemQuantity(item._id, (item.quantity || 1) + 1)}
                                className="w-6 h-6 bg-blue-500 text-white rounded text-sx"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          {/* Quantity Controls */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs">
                              ₹{((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-3xl mb-2">🛒</div>
                        <p className="text-gray-600 font-semibold text-sm mb-1">No items added</p>
                        <p className="text-gray-400 text-xs">
                          Select items from the menu to add them to your order
                        </p>
                      </div>
                    )}

                    <div className="border-t mt-2 pt-2 flex justify-between font-semibold text-sm">
                      <span>Total</span>
                      <span>₹{newOrder.items.reduce((total, item) => total + ((item.price || 0) * (item.quantity || 1)), 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* MOBILE LAYOUT */}
            <div className="md:!hidden grid grid-cols-1 !gap-2 border-b border-gray-200">
              {/* COLUMN 1: CATEGORIES */}
              <div className="pr-2 border-b border-gray-200 pb-2 mb-2">
                <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                  Categories
                </h3>
                <div className="flex gap-2 overflow-x-auto custom-scroll pb-2">
                  {/* All Categories */}
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`flex-shrink-0 border rounded-lg px-3 py-2 text-xs transition whitespace-nowrap
                      ${!selectedCategory
                        ? "bg-blue-500 text-white border-blue-500 shadow"
                        : "bg-white hover:bg-blue-50 border-gray-300"
                      }`}
                  >
                    All Categories
                  </button>

                  {categories.map((category) => (
                    <button
                      key={category._id}
                      onClick={() => setSelectedCategory(category._id)}
                      className={`flex-shrink-0 border rounded-lg px-2 py-1 text-sm transition whitespace-nowrap
                        ${selectedCategory === category._id
                          ? "bg-blue-500 text-white border-blue-500 shadow"
                          : "bg-white hover:bg-blue-50 border-gray-300"
                        }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* COLUMN 2: ITEMS */}
              <div className="border-b border-gray-200 pb-2 mb-2">
                <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                  Menu Items
                </h3>
                <div className="relative mb-2">
                  <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={itemSearchTerm}
                    onChange={(e) => setItemSearchTerm(e.target.value)}
                    className="w-full border rounded-lg pl-10 pr-4 py-2 focus:ring-1 focus:ring-grey-500 outline-none"
                  />
                </div>

                {/* Mobile: Horizontal 3-row layout */}
                <div className="overflow-x-auto custom-scroll pb-2">
                  <div className={`grid ${filteredItems.length > 0 ? 'grid-rows-3' :'grid-rows-1'} grid-flow-col !gap-2 min-w-max`}>

                     {filteredItems.length > 0 ? (
                        filteredItems.map((item) => {
                          const isSelected = newOrder.items.some(orderItem => orderItem._id === item._id);
                          return (
                            <div
                              key={item._id}
                              onClick={() => addItemToOrder(item)}
                              className={`border rounded-lg p-2 cursor-pointer min-w-[140px] max-w-[160px] transition-all ${
                                isSelected 
                                  ? 'bg-blue-50 border-blue-400 shadow-md' 
                                  : 'bg-white hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between !gap-2">
                                <p className="text-xs truncate">
                                  {item.name}
                                </p>
                                <div className="flex items-center gap-1">
                                  <p className="text-blue-500 text-sm whitespace-nowrap">
                                    ₹{item.discountPrice || item.price}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center h-full">
                          <div className="text-3xl mb-2">🔍</div>
                          <p className="text-gray-600 text-sm mb-1">No items found</p>
                          <p className="text-gray-400 text-xs">
                            {selectedCategory && itemSearchTerm
                              ? `No items found in this category matching "${itemSearchTerm}"`
                              : selectedCategory
                                ? 'No items available in this category'
                                : itemSearchTerm
                                  ? `No items matching "${itemSearchTerm}"`
                                  : 'No items available'
                            }
                          </p>
                        </div>
                    )}
                  </div>
                </div>
              </div>

              {/* COLUMN 3: ORDER ITEMS */}
              <div className="">
                <h3 className="text-sm mb-2 pb-1 border-b border-gray-200">
                  Order Items ({newOrder.items.length})
                </h3>
                <div className="divide-y max-h-[320px] overflow-y-auto custom-scroll">
                  {newOrder.items.length > 0 ? (
                    newOrder.items.map((item) => (
                      <div key={item._id} className="p-1">
                        {/* Name + Price + Delete */}
                        <div className="flex items-center justify-between mb-0">
                          <p className="text-xs truncate">
                            {item.name}
                          </p>
                          <div className="flex items-center !gap-0">
                            <button
                              onClick={() => updateItemQuantity(item._id, (item.quantity || 1) - 1)}
                              className="w-6 h-6 bg-gray-200 rounded text-sm"
                            >
                              -
                            </button>
                            <span className="text-xs w-6 text-center">
                              {item.quantity || 1}
                            </span>
                            <button
                              onClick={() => updateItemQuantity(item._id, (item.quantity || 1) + 1)}
                              className="w-6 h-6 bg-blue-500 text-white rounded text-sx"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs">
                            ₹{((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-3xl mb-2">🛒</div>
                      <p className="text-gray-600 font-semibold text-sm mb-1">No items added</p>
                      <p className="text-gray-400 text-xs">
                        Select items from the menu to add them to your order
                      </p>
                    </div>
                  )}

                  <div className="border-t mt-2 pr-2 pt-2 flex justify-between font-semibold text-sm">
                    <span>Total</span>
                    <span>₹{newOrder.items.reduce((total, item) => total + ((item.price || 0) * (item.quantity || 1)), 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
            <button
              onClick={() => setShowCreateOrderModal(false)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-500"
              onClick={handleCreateOrder}
            >
              <FiPlus /> Create Order
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Captain Dashboard</h1>
            <p className="text-gray-600">Welcome, {profile?.name ? toTitleCase(profile.name) : ''}</p>
            {profile?.vendorId?.restaurantDetails?.restaurantName && (
              <p className="text-gray-600">{toTitleCase(profile.vendorId.restaurantDetails.restaurantName)}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="grid grid-cols-3 gap-4 md:gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{tables.length}</div>
                <div className="text-sm text-gray-600">Assigned Tables</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{orders.length}</div>
                <div className="text-sm text-gray-600">Active Orders</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{openTables.length}</div>
                <div className="text-sm text-gray-600">Open Tables</div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                onClick={fetchData}
              >
                <FiRefreshCw size={16} />
                Refresh
              </button>
              <button
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-2 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center gap-2"
                onClick={() => {
                  setSelectedCategory('');
                  setShowCreateOrderModal(true);
                }}
              >
                <FiPlus className="text-lg" />
                Add Order
              </button>
              <button
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                onClick={handleLogout}
              >
                <FiLogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg p-2 mb-6">
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${activeTab === 'tables'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
              }`}
            onClick={() => setActiveTab('tables')}
          >
            <FiGrid size={18} />
            Assigned Tables ({tables.length})
          </button>
          <button
            className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${activeTab === 'orders'
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
              }`}
            onClick={() => setActiveTab('orders')}
          >
            <FiShoppingBag size={18} />
            Active Orders ({orders.length})
          </button>
          <button
            className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${activeTab === 'openTables'
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
              }`}
            onClick={() => setActiveTab('openTables')}
          >
            <FiCoffee size={18} />
            Open Tables ({openTables.length})
          </button>
        </div>
      </div>

      {/* Assigned Tables Tab */}
      {activeTab === 'tables' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Assigned Tables</h2>
          {tables.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiGrid className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No tables assigned</h3>
              <p className="text-gray-600">You haven't been assigned any tables yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {tables.map((table) => {
                // Debug: Check different possible field names for table reference
                const hasActiveOrders = orders.some(order => {
                  // Try different possible field combinations
                  return order.tableId === table._id ||
                    order.tableId?._id === table._id ||
                    order.table === table._id ||
                    order.table?._id === table._id ||
                    order.tableId?.tableNumber === table.tableNumber ||
                    order.tableNumber === table.tableNumber;
                });

                // Debug: Log to console (remove in production)
                console.log(`Table ${table.tableNumber} has orders:`, hasActiveOrders, 'Orders:', orders);

                return (
                  <div key={table._id} className={`rounded-xl p-6 border hover:shadow-lg transition-shadow duration-200 ${hasActiveOrders
                      ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-300'
                      : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
                    }`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-semibold ${hasActiveOrders ? 'text-red-900' : 'text-gray-900'
                        }`}>
                        Table {table.tableNumber}
                      </h3>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${hasActiveOrders
                          ? 'bg-red-100 text-red-800 border border-red-300'
                          : table.isActive
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-gray-100 text-gray-800 border border-gray-200'
                        }`}>
                        {hasActiveOrders ? 'Orders Active' : table.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <button
                      className={`w-full px-4 py-2 text-white rounded-lg font-medium transition-colors duration-200 ${hasActiveOrders
                          ? 'bg-red-500 hover:bg-red-600'
                          : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                      onClick={() => {
                        setSelectedTable(table);
                        setActiveTab('orders');
                      }}
                    >
                      View Orders
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Active Orders Tab */}
      {activeTab === 'orders' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Active Orders</h2>
          {orders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiShoppingBag className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No active orders</h3>
              <p className="text-gray-600">There are no active orders right now.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {orders.map((order) => (
                <div key={order._id} className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Order #{order.orderNumber}</h3>
                      <p className="text-sm text-gray-600">
                        Table {order.tableId?.tableNumber} • {order.customerId?.name || 'Guest'}
                      </p>
                    </div>
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>

                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Items:</h4>
                    <div className="bg-gray-50 rounded-lg p-2 max-h-32 overflow-y-auto">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center py-1 text-sm">
                          <span className="text-gray-700">{item.quantity}x {item.name}</span>
                          <span className="font-medium text-gray-900">₹{item.subtotal}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="text-lg font-bold text-gray-900">
                      Total: ₹{order.pricing.total}
                    </div>
                    <div className="flex gap-2">
                      {order.status === 'pending' && (
                        <button
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200"
                          onClick={() => updateOrderStatus(order._id, 'accepted')}
                        >
                          Accept
                        </button>
                      )}
                      {order.status === 'accepted' && (
                        <button
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200"
                          onClick={() => updateOrderStatus(order._id, 'preparing')}
                        >
                          Start Preparing
                        </button>
                      )}
                      {order.status === 'preparing' && (
                        <button
                          className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors duration-200"
                          onClick={() => updateOrderStatus(order._id, 'ready')}
                        >
                          Mark Ready
                        </button>
                      )}
                      {order.status === 'ready' && (
                        <button
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                          onClick={() => updateOrderStatus(order._id, 'completed')}
                        >
                          <FiCheckCircle size={16} />
                          Complete
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
                    <FiClock size={14} />
                    {new Date(order.createdAt).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Open Tables Tab */}
      {activeTab === 'openTables' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Open Tables</h2>
          {openTables.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCoffee className="text-gray-400 text-3xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No open tables</h3>
              <p className="text-gray-600">There are no open tables right now.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {openTables.map((openTable) => {
                const hasOrders = openTable.orders && openTable.orders.length > 0;
                return (
                  <div
                    key={openTable._id}
                    className={`rounded-xl p-6 border hover:shadow-lg transition-shadow duration-200 ${hasOrders
                        ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-400'
                        : 'bg-gradient-to-br from-purple-50 to-white border-purple-200'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-semibold ${hasOrders ? 'text-red-900' : 'text-gray-900'}`}>
                        Table {openTable.tableId?.tableNumber}
                      </h3>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium border ${hasOrders
                          ? 'bg-red-100 text-red-800 border-red-300'
                          : 'bg-green-100 text-green-800 border-green-200'
                        }`}>
                        {hasOrders ? 'Active' : 'Open'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Customer:</p>
                        <p className="font-medium text-gray-900">{openTable.customerId?.name || 'Guest'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Started:</p>
                        <p className="font-medium text-gray-900">{new Date(openTable.startTime).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Orders:</p>
                        <p className="font-medium text-gray-900">{openTable.orders.length}</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Orders:</h4>
                      <div className="space-y-3">
                        {openTable.orders.map((order, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2 text-sm text-gray-500">
                              <FiClock size={12} />
                              {new Date(order.orderTime).toLocaleTimeString()}
                            </div>
                            <div className="space-y-1">
                              {order.orderId && order.orderId.items && order.orderId.items.length > 0 ? (
                                order.orderId.items.map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">{item.quantity}x {item.name}</span>
                                    <span className="font-medium text-gray-900">₹{item.subtotal}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="text-sm text-gray-500">No items in this order</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="text-lg font-bold text-gray-900 border-t pt-4">
                      Total Amount: ₹{openTable.totalAmount}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CaptainDashboard;
